<template>
  <el-card>
  <div slot="header">
    <el-breadcrumb>
      <el-breadcrumb-item :to="{name: 'users'}">Users</el-breadcrumb-item>
      <el-breadcrumb-item>{{ details.name }}</el-breadcrumb-item>
    </el-breadcrumb>
      <div class="card-toolbar">
        <el-button type="warning" icon="fa fa-code" size="mini" @click="openRawPurchaseResponse()"></el-button>
        <el-button type="primary" icon="fa fa-money" size="mini" @click="openKibanaUserPurchases()"></el-button>
        <el-button type="danger" icon="fa fa-trash" size="mini" @click="remove()"></el-button>
      </div>
  </div>
  <el-tabs>
    <el-tab-pane label="Info">
      <div class="avatar">
        <img :src="avatarUrl" :alt="details.name"/>
      </div>
      <div class="base">
        <h3>{{ details.name }} ({{ details.id }})</h3>
        <p><span>role / stages</span><el-dropdown @command="changeRole">
          <el-button
            type="primary"
            size="mini"
            :disabled="selectedIsCurrent"
            :loading="changeRoleBusy">
            {{ details.role }} / {{ details.stages && details.stages.join(', ') }}
            <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item v-for="role in roles" :key="role" :command="role">
              {{ role }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        </p>
        <p><span>email</span>{{ details.email }}</p>
        <p><span>registered</span>{{ asDate(details.registeredAt) }}</p>
        <p><span>language</span>{{ details.language }}</p>
        <p><span>terms</span><check-icon :checked="details.terms"></check-icon></p>
        <p><span>newsletter</span><check-icon :checked="details.newsletter"></check-icon></p>
        <user-addresses :private="details.privateAddress" :billing="details.billingAddress"></user-addresses>
        <user-profile :profile="details.profile"></user-profile>
      </div>
    </el-tab-pane>
    <el-tab-pane label="Heatmap">
      <img :src="activityMapUrl" class="heatmap">
    </el-tab-pane>
    <el-tab-pane label="Stats">
      <user-stats :stats="stats"></user-stats>
    </el-tab-pane>
    <el-tab-pane label="Purchases">
      <user-purchases :purchases="purchases"></user-purchases>
    </el-tab-pane>
    <el-tab-pane label="Devices">
      <user-devices :devices="devices" @on-clear="clearDevices()"></user-devices>
    </el-tab-pane>
    <el-tab-pane label="">
      <user-devices :devices="devices" @on-clear="clearDevices()"></user-devices>
    </el-tab-pane>    
  </el-tabs>
  </el-card>
</template>

<style lang="scss" scoped>
h3 {
  margin-top: 0;
}

img.heatmap {
  width: 100%;
  max-width: 1032px;
  height: auto;
}

.avatar {
  float: left;
  width: 157px;
  img {
    width: 100%;
  }
}
.base {
  margin-left: 177px;

  & > p {
    margin: 0;
    line-height: 30px;

    & > span {
      display: inline-block;
      width: 120px;
    }
  }

  .user-addresses {
    margin: 20px 0;
  }

  .user-profile {
    margin-bottom: 20px;
  }

  .user-stats {
    margin-bottom: 20px;
  }
}
</style>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'

import CheckIcon from '@/components/shared/CheckIcon'
import UserProfile from './UserProfile'
import UserAddresses from './UserAddresses'
import UserStats from './UserStats'
import UserPurchases from './UserPurchases'
import UserDevices from './UserDevices'

export default {
  components: {
    CheckIcon,
    UserProfile,
    UserAddresses,
    UserStats,
    UserPurchases,
    UserDevices
  },

  data () {
    return {
      roles: ['user', 'beta', 'developer', 'admin'],
      changeRoleBusy: false
    }
  },

  computed: {
    avatarUrl () {
      const uuid = this.details.avatar
      return uuid
        ? this.$store.state.CDN_URL + '/preview/image/' + uuidPathPart(uuid)
        : ''
    },
    activityMapUrl () {
      const id = this.details.id
      return id
        ? this.$store.state.CDN_URL + '/data/activity/user/' + id
        : ''
    },
    selectedIsCurrent () {
      return this.details.id === this.$store.getters['auth/userId']
    },
    ...mapGetters('users', {
      user: 'selected'
    }),
    ...mapState('userDetails', {
      details: state => state,
      stats: state => state.stats,
      purchases: state => state.purchases,
      devices: state => state.devices
    })
  },

  methods: {
    asDate (value, format) {
      return moment(value).format(format || 'lll')
    },
    async changeRole (role) {
      if (role !== this.details.role) {
        try {
          this.changeRoleBusy = true
          await this.setRole(role)
          this.changeRoleBusy = false
        } catch (err) {
          this.changeRoleBusy = false
        }
      }
    },
    openRawPurchaseResponse () {
      let url = `${process.env.API_URL}/simulate/purchases?user_id=${this.user.id}&stage=live&jwt=${this.$store.state.auth.token}`
      window.open(url, 'UserPurchaseDump')
    },
    openKibanaUserPurchases () {
      let url = `${process.env.API_URL}/apilogs/visualize?name=PuchasesAndVouchersPerUser&user_id=${this.user.id}&stage=live&jwt=${this.$store.state.auth.token}`
      window.open(url, 'PuchasesAndVouchersPerUser')
    },
    remove () {
      const msg = 'Please confirm user deletion by entering the user\'s name below. You cannot undo this action.'
      this.$prompt(msg, 'Delete User').then(({value}) => {
        const name = this.user.user_name
        const email = this.details.email
        if (value === name) {
          this.$store.dispatch('users/destroy', email).then(_ => {
            this.$message({
              type: 'success',
              message: `User '${name}' was successfully deleted.`
            })
            this.$router.push({name: 'users'})
          }, _ => {
            this.$message({
              type: 'error',
              message: `Failed to delete user '${name}'.`
            })
          })
        } else {
          this.$message({
            type: 'error',
            message: 'Provided user name does not match. Delete aborted.'
          })
        }
      })
    },
    async clearDevices () {
      try {
        await this.$confirm('The Devices table will be permanently wiped out. Are you sure?', 'Clear Devices')
        this.deleteDevices(this.user.id)
      } catch (err) { }
    },
    ...mapActions('userDetails', {
      fetch: 'fetch',
      clear: 'clear',
      fetchStats: 'fetchStats',
      fetchPurchases: 'fetchPurchases',
      fetchDevices: 'fetchDevices',
      deleteDevices: 'deleteDevices',
      setRole: 'setRole'
    })
  },

  created () {
    this.clear()
    this.fetch(this.user.id)
    this.fetchStats(this.user.id)
    this.fetchPurchases(this.user.id)
    this.fetchDevices(this.user.id)
  },

  destroyed () {
    this.clear()
  }
}

function uuidPathPart (uuid) {
  var parts = uuid.split('-')
  return parts[1] ? parts[0].substring(0, 2) + '/' + parts[1].substring(0, 2) + '/' + uuid : uuid
}
</script>



// WEBPACK FOOTER //
// src/components/users/User.vue